<script setup lang="ts">

import {useAuthStore} from "@/stores/authentication-store";
import {ExitOutline} from "@vicons/ionicons5";
import type {SelectGroupOption, SelectOption} from "naive-ui";
import {onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import TooltipButton from "@/components/shared/TooltipButton.vue";
import {useThemeStore} from "@/stores/theme-store";
import {ScreenSize} from "@/constants/screen-size-helper";
import {useAdministrationOrganizationStore} from "@/stores/administration/organization-store";
import type {IOptionModel} from "@/models/option";
import type {OrganizationListModel} from "@/models/administration/organization/organization";
import AsyncSelect from "@/components/shared/AsyncSelect.vue";

const organizationStore = useAdministrationOrganizationStore();
const authStore = useAuthStore();
const router = useRouter();
const themeStore = useThemeStore();
const {t} = useI18n();

const selectedOrganizationId = ref<number | undefined>(undefined);

onMounted(async () => {
  if (!authStore.hasClaims(['switch-organization.update'])) return;

  await organizationStore.fetchOrganizations();
  selectedOrganizationId.value = authStore.organizationId;
})

const renderLabel = (option: SelectOption | SelectGroupOption) => {
  const selectedOrganization = organizationStore.organizations.items.find(x => x.id === option.value)

  if (selectedOrganization) return selectedOrganization.name;

  return authStore.organizationName;
}

const switchOrganization = async (organizationId: number) => {
  if (!Number.isFinite(organizationId)) return;

  const success = await authStore.switchOrganization(organizationId);

  if (success) {
    // Refresh the page
    router.go(0);
  }
}

const switchOrganizationFromSelectValue = async (organizationId: unknown) => {
  const organizationIdNumber = typeof organizationId === 'number' ? organizationId : Number(organizationId);
  if (!Number.isFinite(organizationIdNumber)) return;
  await switchOrganization(organizationIdNumber);
}

const returnToOwnOrganization = async () => {
  if (authStore.originalOrganizationId) {
    await switchOrganization(authStore.originalOrganizationId);
  }
}

const convertToOptions = (items: OrganizationListModel[]): IOptionModel<number>[] => {
  return items.filter(x => x.isProvisioned).map(item => ({
    value: item.id,
    label: item.name,
  }))
}
</script>

<template>
  <tooltip-button v-if="authStore.hasSwitchedOrganization() && themeStore.screenSize == ScreenSize.Default"
                  :tooltip="t('components.layout.organization_switch.return_to_own_organization')"
                  :icon="ExitOutline"
                  shape="circle"
                  buttonStyle="primary"
                  type="primary"
                  size="small"
                  @click="returnToOwnOrganization"/>

  <async-select v-check-claim="['switch-organization.update']"
                :value="selectedOrganizationId"
                :loading="organizationStore.loading"
                :page="organizationStore.organizationListRequest.page"
                :pageCount="organizationStore.organizations.pageCount"
                :fetch-function="organizationStore.fetchOrganizations"
                :fetch-and-append-function="organizationStore.fetchAndAppendOrganizations"
                :options="convertToOptions(organizationStore.organizations.items)"
                :render-label-function="renderLabel"
                :update-value-function="switchOrganizationFromSelectValue"
                :placeholder="t('components.layout.organization_switch.select_organization')"
                :clearable="false"
                size="small"
                @update:value="selectedOrganizationId = $event"
                @update:loading="organizationStore.loading = $event"
                @update:search="organizationStore.organizationListRequest.name = $event"
                @update:page="organizationStore.organizationListRequest.page = $event"/>
</template>

<style scoped lang="scss">
</style>
